import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack, Typography } from "@mui/material"

import type { Conductor } from "types/conductors.types"

import LabelValueStack from "widgets/common/LabelValueStack"
import SimpleTable from "widgets/common/SimpleTable"
import {
  type SpecGroup,
  conductorEnumLabel,
  getConductorLayerColumns,
  getConductorResistanceColumns,
  getConductorSpecGroups,
} from "helpers/utils/conductors"
import ConductorSpecGroup from "./ConductorSpecGroup"

interface ConductorSpecProps {
  conductor: Conductor
}

/**
 * A conductor read as its datasheet: what identifies it, then its properties by
 * group. The power line opens it in a dialog, the span page lays it out in a
 * section, so it brings no chrome of its own.
 */
const ConductorSpec = ({ conductor }: ConductorSpecProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  const resistanceColumns = useMemo(
    () => getConductorResistanceColumns(t, locale),
    [t, locale],
  )
  const layerColumns = useMemo(() => getConductorLayerColumns(t, locale), [t, locale])

  const { groups, record, unknownFields } = useMemo(
    () => getConductorSpecGroups(conductor, locale),
    [conductor, locale],
  )

  const groupFor = (titleKey: string) =>
    groups.find((group) => group.titleKey === titleKey)

  // A row of two halves, so a value sits the same distance from its label
  // whether or not the half beside it holds a group.
  const specRow = (left?: SpecGroup, right?: SpecGroup) =>
    (left || right) && (
      <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems="flex-start">
        {[left, right].map((group, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              display: group ? "block" : { xs: "none", md: "block" },
            }}
          >
            {group && (
              <ConductorSpecGroup titleKey={group.titleKey} fields={group.fields} />
            )}
          </Box>
        ))}
      </Stack>
    )

  const subtitle = [
    conductor.name,
    conductor.manufacturer,
    conductorEnumLabel(t, "STANDARDS", conductor.standard),
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h6">
          {conductor.type} — {conductorEnumLabel(t, "TYPES", conductor.type)}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="neutral.600">
            {subtitle}
          </Typography>
        )}
      </Stack>
      {specRow(groupFor("conductors.GEOMETRY"), record)}
      {conductor.layers.length > 0 && (
        <ConductorSpecGroup titleKey="conductors.LAYERS">
          {conductor.greasing && (
            <LabelValueStack
              labelKey="conductors.GREASING"
              value={
                <Typography color="neutral.600">
                  {conductorEnumLabel(t, "GREASINGS", conductor.greasing)}
                </Typography>
              }
            />
          )}
          <SimpleTable
            columns={layerColumns}
            rows={conductor.layers}
            getRowKey={(layer, index) => layer.layer_index ?? index}
          />
        </ConductorSpecGroup>
      )}
      {specRow(
        groupFor("conductors.THERMAL_AND_RADIATIVE"),
        groupFor("conductors.MECHANICAL"),
      )}
      {/* The resistance table wants the whole width, so it sits below the
          columns. */}
      {conductor.resistance_specs.length > 0 && (
        <ConductorSpecGroup titleKey="conductors.ELECTRICAL_RESISTANCE">
          <SimpleTable
            columns={resistanceColumns}
            rows={conductor.resistance_specs}
            getRowKey={(spec) => `${spec.current_type}-${spec.at_temp}`}
          />
        </ConductorSpecGroup>
      )}
      {unknownFields.length > 0 && (
        <ConductorSpecGroup titleKey="conductors.OTHER" fields={unknownFields} />
      )}
    </Stack>
  )
}

export default ConductorSpec
